import React, { useState, useMemo } from 'react';
import { Rule, MarketSnapshot } from '../types';
import { MarketDataState } from '../hooks/useMarketData';
import { evaluateRule } from '../utils/ruleEngine';
import useCollection from '../hooks/useCollection';
import * as Actions from '../firebase/actions';
import ConfirmationModal from './ConfirmationModal';
import TechnicalAnalysis from './TechnicalAnalysis';
import { SUPPORTED_SYMBOLS } from '../hooks/useMarketData';

interface RuleSignalPageProps {
  marketData: MarketDataState;
  isAdmin: boolean;
}

const emptyForm = (): Omit<Rule, 'id'> => ({
  name: '',
  signalType: 'long',
  enabled: true,
});

const TrendSelect: React.FC<{
  value: 'up' | 'down' | '';
  onChange: (v: 'up' | 'down' | undefined) => void;
  label: string;
}> = ({ value, onChange, label }) => (
  <div>
    <label className="block text-xs text-gray-500 mb-1">{label}</label>
    <select
      value={value}
      onChange={e => onChange(e.target.value === '' ? undefined : e.target.value as 'up' | 'down')}
      className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-fuchsia-300"
    >
      <option value="">조건 없음</option>
      <option value="up">증가 ▲</option>
      <option value="down">감소 ▼</option>
    </select>
  </div>
);

const RuleSignalPage: React.FC<RuleSignalPageProps> = ({ marketData, isAdmin }) => {
  const rules = useCollection<Rule>('rules', 'name', 'asc');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<Omit<Rule, 'id'>>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<string | null>(null);
  const [selectedCoin, setSelectedCoin] = useState('BTC');

  const snapshot: MarketSnapshot | null = marketData.snapshots[selectedCoin] ?? null;

  const handleAddRule = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await Actions.addRule(form);
      setForm(emptyForm());
      setShowForm(false);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (rule: Rule) => {
    if (!isAdmin) return;
    await Actions.toggleRule(rule.id, !rule.enabled);
  };

  const handleDelete = async () => {
    if (!ruleToDelete) return;
    await Actions.deleteRule(ruleToDelete);
    setRuleToDelete(null);
  };

  const setField = <K extends keyof Omit<Rule, 'id'>>(key: K, value: Omit<Rule, 'id'>[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="space-y-6">
      {/* 기술적 분석 게이지 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {SUPPORTED_SYMBOLS.map(coin => (
          <div key={coin} className="bg-white/60 backdrop-blur border border-white/30 rounded-2xl shadow p-4">
            <h3 className="text-sm font-bold text-gray-700 mb-2">📊 {coin} 기술적 분석</h3>
            <TechnicalAnalysis symbol={coin} />
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">룰 시그널</h2>
          <p className="text-sm text-gray-500 mt-0.5">조건을 등록하면 마켓 데이터와 비교해 시그널을 발동합니다</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1">
            {SUPPORTED_SYMBOLS.map(c => (
              <button
                key={c}
                onClick={() => setSelectedCoin(c)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${selectedCoin === c ? 'bg-fuchsia-500 text-white' : 'bg-white border border-gray-200 text-gray-500'}`}
              >
                {c}
              </button>
            ))}
          </div>
          {isAdmin && (
            <button
              onClick={() => setShowForm(v => !v)}
              className="px-4 py-2 rounded-xl bg-fuchsia-500 hover:bg-fuchsia-600 text-white text-sm font-bold transition-colors"
            >
              {showForm ? '✕ 취소' : '+ 룰 추가'}
            </button>
          )}
        </div>
      </div>

      {/* 룰 추가 폼 */}
      {showForm && isAdmin && (
        <div className="bg-white/70 backdrop-blur border border-fuchsia-100 rounded-2xl shadow p-5 space-y-4">
          <h3 className="text-sm font-bold text-fuchsia-700">새 룰 등록</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-xs text-gray-500 mb-1">룰 이름 *</label>
              <input
                value={form.name}
                onChange={e => setField('name', e.target.value)}
                placeholder="예: 펀딩 고점 숏"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-300"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">펀딩비 최소 (%)</label>
              <input
                type="number"
                step="0.01"
                value={form.fundingMin ?? ''}
                onChange={e => setField('fundingMin', e.target.value === '' ? undefined : parseFloat(e.target.value))}
                placeholder="예: 0.03"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-300"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">펀딩비 최대 (%)</label>
              <input
                type="number"
                step="0.01"
                value={form.fundingMax ?? ''}
                onChange={e => setField('fundingMax', e.target.value === '' ? undefined : parseFloat(e.target.value))}
                placeholder="예: 0.1"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-300"
              />
            </div>
            <TrendSelect label="OI 추세" value={form.oiTrend ?? ''} onChange={v => setField('oiTrend', v)} />
            <TrendSelect label="Liq 추세" value={form.liqTrend ?? ''} onChange={v => setField('liqTrend', v)} />
            <TrendSelect label="가격 추세" value={form.priceTrend ?? ''} onChange={v => setField('priceTrend', v)} />
            <div>
              <label className="block text-xs text-gray-500 mb-1">시그널 타입 *</label>
              <select
                value={form.signalType}
                onChange={e => setField('signalType', e.target.value as 'long' | 'short')}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-300"
              >
                <option value="long">🟢 LONG</option>
                <option value="short">🔴 SHORT</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => { setShowForm(false); setForm(emptyForm()); }} className="px-4 py-2 rounded-lg text-sm text-gray-500 hover:bg-gray-100 transition-colors">취소</button>
            <button
              onClick={handleAddRule}
              disabled={saving || !form.name.trim()}
              className="px-5 py-2 rounded-lg bg-fuchsia-500 hover:bg-fuchsia-600 text-white text-sm font-bold disabled:opacity-50 transition-colors"
            >
              {saving ? '저장 중...' : '저장'}
            </button>
          </div>
        </div>
      )}

      {/* 룰 목록 */}
      {rules.length === 0 ? (
        <div className="bg-white/60 backdrop-blur border border-white/30 rounded-2xl shadow p-12 text-center text-gray-400">
          <p className="text-4xl mb-3">📋</p>
          <p className="font-medium">등록된 룰이 없습니다</p>
          {isAdmin && <p className="text-sm mt-1">위의 '+ 룰 추가' 버튼으로 룰을 만들어보세요</p>}
        </div>
      ) : (
        <div className="bg-white/60 backdrop-blur border border-white/30 rounded-2xl shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50/80 text-xs text-gray-500 border-b border-gray-100">
                  <th className="text-left px-4 py-3 font-semibold">이름</th>
                  <th className="text-center px-4 py-3 font-semibold">펀딩 범위</th>
                  <th className="text-center px-4 py-3 font-semibold">OI</th>
                  <th className="text-center px-4 py-3 font-semibold">Liq</th>
                  <th className="text-center px-4 py-3 font-semibold">가격</th>
                  <th className="text-center px-4 py-3 font-semibold">시그널</th>
                  <th className="text-center px-4 py-3 font-semibold">발동</th>
                  {isAdmin && <th className="text-center px-4 py-3 font-semibold">ON/OFF</th>}
                  {isAdmin && <th className="text-center px-4 py-3 font-semibold">삭제</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {rules.map(rule => {
                  const active = snapshot ? evaluateRule(rule, snapshot) : false;
                  return (
                    <tr key={rule.id} className={`transition-colors ${!rule.enabled ? 'opacity-40' : active ? 'bg-yellow-50/50' : ''}`}>
                      <td className="px-4 py-3 font-medium text-gray-800">{rule.name}</td>
                      <td className="px-4 py-3 text-center text-xs text-gray-500 font-mono">
                        {rule.fundingMin !== undefined || rule.fundingMax !== undefined
                          ? `${rule.fundingMin ?? '—'} ~ ${rule.fundingMax ?? '—'}%`
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {rule.oiTrend ? <span className={rule.oiTrend === 'up' ? 'text-green-600' : 'text-red-500'}>{rule.oiTrend === 'up' ? '▲' : '▼'}</span> : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {rule.liqTrend ? <span className={rule.liqTrend === 'up' ? 'text-green-600' : 'text-red-500'}>{rule.liqTrend === 'up' ? '▲' : '▼'}</span> : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {rule.priceTrend ? <span className={rule.priceTrend === 'up' ? 'text-green-600' : 'text-red-500'}>{rule.priceTrend === 'up' ? '▲' : '▼'}</span> : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${rule.signalType === 'long' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {rule.signalType === 'long' ? '🟢 LONG' : '🔴 SHORT'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {rule.enabled
                          ? active
                            ? <span className="text-yellow-500 font-bold text-xs">⚡ 발동</span>
                            : <span className="text-gray-300 text-xs">대기</span>
                          : <span className="text-gray-300 text-xs">OFF</span>}
                      </td>
                      {isAdmin && (
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => handleToggle(rule)}
                            className={`w-10 h-5 rounded-full transition-colors relative ${rule.enabled ? 'bg-fuchsia-500' : 'bg-gray-200'}`}
                          >
                            <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${rule.enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                          </button>
                        </td>
                      )}
                      {isAdmin && (
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => setRuleToDelete(rule.id)}
                            className="text-gray-300 hover:text-red-400 transition-colors text-lg"
                          >
                            ✕
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={!!ruleToDelete}
        onClose={() => setRuleToDelete(null)}
        onConfirm={handleDelete}
        title="룰 삭제"
        message="이 룰을 삭제하시겠습니까? 되돌릴 수 없습니다."
      />
    </div>
  );
};

export default RuleSignalPage;
